import { DOCUMENT } from '@angular/common';
import {
    disconnectExpandedDetails,
    observeExpandedDetail,
} from 'src/app/shared/expanded-detail-resizer';
import { Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TransactionStatus } from 'src/app/enums/transaction.status';
import { TransactionType } from 'src/app/enums/transaction.type';
import {
    BalanceSnapshot,
    balanceAmount,
    isBalanceAvailable,
    UNAVAILABLE_DISPLAY,
} from 'src/app/model/cashback-allocation-balance';
import { Transaction } from 'src/app/model/transaction';
import { CommonUtil } from 'src/app/services/common.util';
import { CompanyService } from 'src/app/services/company.service';

@Component({
    selector: 'app-transaction-body',
    templateUrl: './transaction-body.component.html',
    styleUrls: [
        './transaction-body.component.css',
        // LP-5360: the allocation-table rules moved here so the Enterprise view can share them.
        // Component styles are scoped, so a second view cannot see rules living in this component.
        '../../shared/styles/cashback-allocation-table.css',
        '../../shared/styles/transaction-detail-layout.css',
    ],
})
export class TransactionBodyComponent implements OnInit, OnChanges, OnDestroy {
    @Input() transactionList: Array<Transaction> = [];
    status = TransactionStatus;
    groupIdColours = {};
    transactionType = TransactionType;

    /** LP-5358: the marker shown for a balance the backend does not have. Never `0.00`. */
    unavailableDisplay = UNAVAILABLE_DISPLAY;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private document: Document,
        public commonUtil: CommonUtil,
        public companyService: CompanyService,
    ) { }

    ngOnInit() {
        if (this.transactionList) {
            this.groupIdColours = this.commonUtil.getTxnGroupIdColours(this.transactionList);
        }
    }

    /**
     * LP-5360: a new list means the previously observed rows are gone. Observers must not outlive
     * them -- a ResizeObserver with a detached target keeps that element and its Angular view
     * alive, which accumulates across pagination and searches. Codex PR #114, round 2.
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes['transactionList']) {
            disconnectExpandedDetails(this.elementRef.nativeElement);
        }
    }

    ngOnDestroy() {
        disconnectExpandedDetails(this.elementRef.nativeElement);
    }

    showDetails(val: string, logo: string) {
        const element = this.document.getElementById(val);
        if (element) {
            if (element.style.maxHeight) {
                element.style.maxHeight = '';
                element.style.border = 'none';
            } else {
                element.style.maxHeight = element.scrollHeight + 'px';
                element.style.border = '6px';
                // LP-5360: the container queries make this panel taller as it narrows, so a row
                // left open while the window or sidebar changes would be clipped at the height it
                // had when it was wider. Codex PR #114, P2.
                observeExpandedDetail(element);
            }
        }

        const logoElement = this.document.getElementById(logo);
        if (logoElement) {
            let src = logoElement.getAttribute('src');
            src =
                src === 'assets/images/default-transaction-detail.png'
                    ? 'assets/images/hover-transaction-detail.png'
                    : 'assets/images/default-transaction-detail.png';

            logoElement.setAttribute('src', src);
        }
    }

    copyText(val: string) {
        const input = this.document.createElement('input');
        this.document.body.appendChild(input);
        if (val) {
            input.value = val;
        } else {
            input.value = 'NA';
        }
        input.select();
        this.document.execCommand('copy');
        input.remove();
    }

    /**
     * LP-5358: true when the backend sent an authoritative value for this balance.
     *
     * <p>The template branches on this rather than on the amount, so an authoritative `0` renders
     * as `0.00` while an absent value renders as the unavailable marker. Truthiness checks would
     * collapse those two into one.
     */
    hasBalance(snapshot: BalanceSnapshot): boolean {
        return isBalanceAvailable(snapshot);
    }

    /** The value for the `number` pipe; null whenever {@link hasBalance} is false. */
    balanceValue(snapshot: BalanceSnapshot): number | null {
        return balanceAmount(snapshot);
    }

}
